import React from 'react';
import XMLCodeEditor from '../XMLParser/XMLCodeEditor';
import { IconPlus, IconX, IconAlertTriangle } from '@tabler/icons-react';

export const AddRulesModal = ({
                                  showModal,
                                  additionalXml,
                                  setAdditionalXml,
                                  addRulesErrors,
                                  availableCollections,
                                  selectedCollection,
                                  setSelectedCollection,
                                  onClose,
                                  onAddRules,
                                  duplicateWarning,
                                  onConfirmDuplicateAdd,
                                  onCancelDuplicateAdd
                              }) => {
    const handleClose = () => {
        onClose();
    };

    if (!showModal) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9998,
                    backgroundColor: 'rgba(0,0,0,0.5)'
                }}
                onClick={handleClose}
            ></div>
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                }}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered"
                    style={{
                        pointerEvents: 'auto',
                        position: 'relative',
                        zIndex: 10000
                    }}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">
                                {duplicateWarning ? 'Duplicate Rules Detected' : 'Add Additional XML Rules'}
                            </h4>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleClose}
                            >
                                <IconX className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="modal-body">
                            {duplicateWarning ? (
                                <div>
                                    <div className="alert alert-warning d-flex align-items-center">
                                        <IconAlertTriangle className="w-5 h-5 me-2" />
                                        <div>
                                            <strong>Potential duplicate rules found!</strong>
                                            <div className="text-muted">The following similarities were detected:</div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        {duplicateWarning.map((duplicate, idx) => (
                                            <div key={idx} className="card mb-2">
                                                <div className="card-body p-3">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <span className="badge bg-orange text-white me-2">
                                                                {duplicate.type}
                                                            </span>
                                                            <div className="text-sm text-muted mt-1">
                                                                {duplicate.details}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2">
                                                        <div className="text-sm">
                                                            <strong>New:</strong> {duplicate.newRuleName}
                                                        </div>
                                                        <div className="text-sm text-muted">
                                                            <strong>Existing:</strong> {duplicate.existingRuleName}
                                                        </div>
                                                    </div>

                                                    {/* Matching Conditions */}
                                                    {duplicate.matchingConditions && duplicate.matchingConditions.length > 0 && (
                                                        <div className="mt-3">
                                                            <div className="text-sm font-semibold text-blue-600 mb-1">
                                                                Matching Conditions:
                                                            </div>
                                                            {duplicate.matchingConditions.map((condition, condIdx) => (
                                                                <div key={condIdx} className="text-xs bg-blue-50 p-2 rounded mb-1">
                                                                    <span className="badge badge-outline text-blue me-1">
                                                                        {condition.type}
                                                                    </span>
                                                                    {condition.details}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Matching Exceptions */}
                                                    {duplicate.matchingExceptions && duplicate.matchingExceptions.length > 0 && (
                                                        <div className="mt-3">
                                                            <div className="text-sm font-semibold text-red-600 mb-1">
                                                                Matching Exceptions:
                                                            </div>
                                                            {duplicate.matchingExceptions.map((exception, excIdx) => (
                                                                <div key={excIdx} className="text-xs bg-red-50 p-2 rounded mb-1">
                                                                    <span className="badge badge-outline text-red me-1">
                                                                        {exception.type}
                                                                    </span>
                                                                    {exception.details}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="alert alert-info">
                                        <strong>Do you want to proceed?</strong>
                                        <div className="text-muted">
                                            Adding duplicate rules may cause conflicts or unexpected behavior in AppLocker policies.
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-muted mb-3">
                                        Paste individual AppLocker XML rules here. Only single rules are supported:
                                    </p>
                                    <ul className="text-muted mb-3">
                                        <li><code>&lt;FilePublisherRule&gt;</code> - Publisher-based rules</li>
                                        <li><code>&lt;FilePathRule&gt;</code> - Path-based rules</li>
                                        <li><code>&lt;FileHashRule&gt;</code> - Hash-based rules</li>
                                    </ul>

                                    {availableCollections.length > 1 && (
                                        <div className="mb-3">
                                            <label className="form-label">Target Collection</label>
                                            <select
                                                className="form-select"
                                                value={selectedCollection}
                                                onChange={(e) => setSelectedCollection(e.target.value)}
                                            >
                                                {availableCollections.map(collection => (
                                                    <option key={collection.type} value={collection.type}>
                                                        {collection.type} ({collection.enforcementMode})
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="form-hint">
                                                Select which RuleCollection should receive the new rules
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ position: 'relative', zIndex: 10001 }}>
                                        <XMLCodeEditor
                                            value={additionalXml}
                                            onChange={setAdditionalXml}
                                        />
                                    </div>

                                    {addRulesErrors.length > 0 && (
                                        <div className="mt-3 text-red-600">
                                            {addRulesErrors.map((err, idx) => (
                                                <p key={idx} className="mb-2">🚨 {err}</p>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            {duplicateWarning ? (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={onCancelDuplicateAdd}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-warning"
                                        onClick={onConfirmDuplicateAdd}
                                    >
                                        <IconAlertTriangle className="w-4 h-4 me-1" />
                                        Add Anyway
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={onAddRules}
                                        disabled={!additionalXml.trim()}
                                    >
                                        <IconPlus className="w-4 h-4 me-1" />
                                        Add Rules
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};